import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getPlayersByTeam, addPlayer, deletePlayer } from "../services/playerService";
import { useTheme } from "../config/ThemeContext";
import apiClient from "../services/apiClient";
import CreatePlayerForm from "./CreatePlayerForm";
import EditPlayerDialog from "./EditPlayerDialog";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

export default function PlayerManagement() {
  const { teamId } = useParams();
  const colors = useTheme();
  const [players, setPlayers] = useState([]);
  const [teamName, setTeamName] = useState("");
  const [editingPlayer, setEditingPlayer] = useState(null);

  const fetchTeam = async () => {
    try {
      const { data } = await apiClient.get(`/api/teams/${teamId}`);
      setTeamName(data.name);
    } catch (err) {
      console.error("Failed to fetch team name", err.message);
    }
  };

  const fetchPlayers = async () => {
    try {
      const data = await getPlayersByTeam(teamId);
      setPlayers(data);
    } catch (err) {
      console.error("Failed to fetch players", err.message);
    }
  };

  const handleCreate = async (newPlayer) => {
    try {
      const player = await addPlayer({ ...newPlayer, teamId });
      setPlayers((prev) => [...prev, player]);
    } catch {
      alert("Failed to add player.");
    }
  };

  const handleEdit = async (updatedPlayer) => {
    try {
      const res = await apiClient.put(`/api/players/${updatedPlayer.id}`, updatedPlayer);
      setPlayers((prev) =>
        prev.map((p) => (p.id === updatedPlayer.id ? res.data : p))
      );
      setEditingPlayer(null);
    } catch (err) {
      alert("Failed to update player.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this player?")) return;
    try {
      await deletePlayer(id);
      setPlayers((prev) => prev.filter((p) => p.id !== id));
    } catch {
      alert("Failed to delete player.");
    }
  };

  useEffect(() => {
    fetchPlayers();
    fetchTeam();
  }, [teamId]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="relative text-center mb-8">
        <h1 className={`text-3xl font-bold ${colors.neutral.text} mb-2`}>
          Players for {teamName}
        </h1>
        <div className={`h-1 w-20 ${colors.primary.bg} mx-auto mt-4`} />
        <div className="absolute right-0 top-0">
          <a
            href={`/teams/${teamId}/report`}
            target="_blank"
            rel="noopener noreferrer"
            className={`${colors.buttons.secondary} inline-block px-4 py-2 rounded shadow`}
          >
            📄 O2 Report
          </a>
        </div>
      </div>


      <div className={`bg-white rounded-xl shadow-md p-6 border-t-4 ${colors.secondary.border}`}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              {["Shirt #", "Last", "First", "Shirt Name", "Position", "Birthdate", "Weight", "Height", "Spike", "Block", "Captain", "Actions"].map((h) => (
                <th key={h} className={`px-3 py-3 text-left text-xs font-medium ${colors.neutral.text}`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {players.map((p) => (
              <tr key={p.id}>
                <td className="px-3 py-2">{p.shirtNumber}</td>
                <td className="px-3 py-2">{p.lastName}</td>
                <td className="px-3 py-2">{p.firstName}</td>
                <td className="px-3 py-2">{p.shirtName}</td>
                <td className="px-3 py-2">{p.position}</td>
                <td className="px-3 py-2">{p.birthdate}</td>
                <td className="px-3 py-2">{p.weight}</td>
                <td className="px-3 py-2">{p.height}</td>
                <td className="px-3 py-2">{p.spikeReach}</td>
                <td className="px-3 py-2">{p.blockReach}</td>
                <td className="px-3 py-2">{p.isCaptain ? "✅" : "-"}</td>
                <td className="px-3 py-2 space-x-2">
                  <button onClick={() => setEditingPlayer(p)} className={colors.buttons.primary}>✏️</button>
                  <button onClick={() => handleDelete(p.id)} className={colors.buttons.danger}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CreatePlayerForm
		  onCreate={handleCreate}
		  teamId={teamId}
		  setPlayers={setPlayers}
		  getPlayersByTeam={getPlayersByTeam}
		  refreshPlayers={fetchPlayers}
		/>


      {editingPlayer && (
        <EditPlayerDialog
          player={editingPlayer}
          onSave={handleEdit}
          onCancel={() => setEditingPlayer(null)}
        />
      )}
    </div>
  );
}
